// Copyright 2026 Bitech.id. Licensed under the MIT License.

import type { BiwaveClient } from '@biwave/core';
import { installSessionEventCapture } from './events.js';
import type { SessionEvent } from './events.js';
import { gzipJson } from './gzip.js';
import { startRecorder } from './recorder.js';
import { RingBuffer } from './ring-buffer.js';
import { SessionManager } from './session-manager.js';
import { setErrorCapturedHook, setReplaySessionId, setStopRequestedHook } from './session-ref.js';
import { StreamTransport } from './transport.js';
import type { ResolvedStreamOptions, StreamEndReason } from './types.js';

const FLUSH_INTERVAL_MS = 10_000;
// Heuristic proxy for a ~256-512KB gzipped chunk — rrweb DOM mutation JSON
// typically compresses 5-10x, so this is checked periodically (not on every
// event, which would mean re-stringifying the whole buffer constantly) and
// is only ever a *trigger* for an early flush; the actual 1MB cap is
// enforced against the real gzipped size in uploadEventsInBudget().
const TARGET_CHUNK_RAW_BYTES = 2 * 1024 * 1024;
const SIZE_CHECK_EVERY_N_EVENTS = 20;

// Session events (console/network/click/navigation) — per the ingest
// contract: batched ±5s / 50 events, max 500 per request, soft cap of
// 10k per session (beyond it the SDK just stops capturing).
const SESSION_EVENTS_FLUSH_INTERVAL_MS = 5_000;
const SESSION_EVENTS_BATCH_SIZE = 50;
const SESSION_EVENTS_MAX_PER_REQUEST = 500;
const SESSION_EVENTS_MAX_PER_SESSION = 10_000;

interface RRwebEventLike {
  timestamp?: number;
}

function detectDevice(): { os: string; browser: string; model: null } {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';

  let os = 'unknown';
  if (/Windows/.test(ua)) os = 'Windows';
  else if (/Mac OS X/.test(ua)) os = 'macOS';
  else if (/Android/.test(ua)) os = 'Android';
  else if (/iPhone|iPad|iPod/.test(ua)) os = 'iOS';
  else if (/Linux/.test(ua)) os = 'Linux';

  let browser = 'unknown';
  if (/Edg\//.test(ua)) browser = 'Edge';
  else if (/Chrome\//.test(ua)) browser = 'Chrome';
  else if (/Firefox\//.test(ua)) browser = 'Firefox';
  else if (/Safari\//.test(ua) && !/Chrome/.test(ua)) browser = 'Safari';

  return { os, browser, model: null };
}

export interface StreamControllerDeps {
  client: BiwaveClient;
  clientId: string;
  endpoint: string;
  environment: string | undefined;
  sdkVersion: string;
  options: ResolvedStreamOptions;
}

/**
 * Owns a single Stream (session replay) session end to end: sampling,
 * recording, chunk buffering/flushing, the buffered-mode ring buffer and
 * its promote-on-error path, and session teardown. Instantiated by
 * @biwave/browser's init() via a dynamic import — see index.ts — so apps
 * that don't enable Stream never load rrweb.
 */
export class StreamController {
  private readonly sessionManager: SessionManager;
  private readonly transport: StreamTransport;
  private readonly client: BiwaveClient;
  private readonly environment: string | undefined;
  private readonly sdkVersion: string;
  private readonly options: ResolvedStreamOptions;
  private readonly endpoint: string;

  private ringBuffer: RingBuffer | null = null;
  private currentChunkEvents: unknown[] = [];
  private eventsSinceSizeCheck = 0;
  private stopRecorder: (() => void) | null = null;
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private durationTimer: ReturnType<typeof setTimeout> | null = null;
  private ended = false;

  // Session events (console/network/click/navigation) — a second, lighter
  // pipeline alongside the rrweb chunk stream, mirroring its two modes:
  // pending array + flush timer when recording, ring buffer when buffered.
  private pendingSessionEvents: SessionEvent[] = [];
  private sessionEventsRing: RingBuffer | null = null;
  private stopEventCapture: (() => void) | null = null;
  private sessionEventsFlushTimer: ReturnType<typeof setInterval> | null = null;
  private sessionEventCount = 0;

  private readonly handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') this.flushForUnload();
  };

  private readonly handlePageHide = (): void => {
    if (this.ended) return;
    this.flushForUnload();

    const state = this.sessionManager.getState();
    if (!state || state.mode !== 'recording') return;

    this.stopRecorder?.();
    this.stopEventCapture?.();
    if (this.flushTimer) clearInterval(this.flushTimer);
    if (this.sessionEventsFlushTimer) clearInterval(this.sessionEventsFlushTimer);
    if (this.durationTimer) clearTimeout(this.durationTimer);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handlePageHide);

    setReplaySessionId(undefined);
    setErrorCapturedHook(undefined);
    setStopRequestedHook(undefined);

    this.transport.endSession(state.session_id, {
      ended_at: Date.now(),
      last_seq: Math.max(0, state.next_seq - 1),
      chunk_count: state.next_seq,
      reason: 'normal',
    });

    this.sessionManager.end();
    this.ended = true;
  };

  constructor(deps: StreamControllerDeps) {
    this.client = deps.client;
    this.environment = deps.environment;
    this.sdkVersion = deps.sdkVersion;
    this.options = deps.options;
    this.endpoint = deps.endpoint;
    this.sessionManager = new SessionManager(deps.options);
    this.transport = new StreamTransport(`${deps.endpoint}/api`, deps.clientId);
  }

  /** Never throws — any internal failure is swallowed (console.debug only), per the SDK's own contract. */
  start(): void {
    try {
      this.startUnsafe();
    } catch (err) {
      console.debug('[Biwave Stream] failed to start', err);
    }
  }

  private startUnsafe(): void {
    const state = this.sessionManager.start();
    if (!state) return; // sampled out entirely — no recording, no buffering

    setReplaySessionId(state.session_id);
    setErrorCapturedHook(() => this.onErrorCaptured());
    setStopRequestedHook((reason) => this.stop(reason));

    if (state.mode === 'buffered') {
      this.ringBuffer = new RingBuffer(this.options.bufferSeconds);
      this.sessionEventsRing = new RingBuffer(this.options.bufferSeconds);
    } else if (!state.init_sent) {
      void this.sendInit(state.session_id, state.started_at);
    }

    this.stopRecorder = startRecorder({
      maskAllText: this.options.maskAllText,
      onEvent: (event, isCheckout) => this.onEvent(event, isCheckout),
    });

    if (this.options.captureEvents) {
      this.stopEventCapture = installSessionEventCapture({
        ignoreOrigin: new URL(this.endpoint).origin,
        maskAllText: this.options.maskAllText,
        onEvent: (event) => this.onSessionEvent(event),
      });
      this.sessionEventsFlushTimer = setInterval(
        () => this.flushSessionEvents(),
        SESSION_EVENTS_FLUSH_INTERVAL_MS,
      );
      (this.sessionEventsFlushTimer as unknown as { unref?: () => void }).unref?.();
    }

    this.flushTimer = setInterval(() => void this.flushCurrentChunk(), FLUSH_INTERVAL_MS);
    (this.flushTimer as unknown as { unref?: () => void }).unref?.();

    const limits = this.sessionManager.getLimits();
    const remainingMs = Math.max(
      0,
      limits.max_session_duration_ms - (Date.now() - state.started_at),
    );
    this.durationTimer = setTimeout(() => this.stop('normal'), remainingMs);

    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    window.addEventListener('pagehide', this.handlePageHide);
  }

  private async sendInit(sessionId: string, startedAt: number): Promise<void> {
    const response = await this.transport.initSession({
      session_id: sessionId,
      platform: 'web',
      capture_mode: 'dom',
      environment: this.environment,
      started_at: startedAt,
      sdk: { name: 'biwave-browser', version: this.sdkVersion },
      device: detectDevice(),
      user: this.client.scope.getUser(),
    });

    if (response) {
      this.sessionManager.setLimits(response.limits);
    }
  }

  private onEvent(event: unknown, _isCheckout: boolean): void {
    const state = this.sessionManager.getState();
    if (!state) return;

    if (state.mode === 'buffered') {
      this.ringBuffer?.add(event, Date.now());
      return;
    }

    this.currentChunkEvents.push(event);
    this.eventsSinceSizeCheck += 1;

    if (this.eventsSinceSizeCheck >= SIZE_CHECK_EVERY_N_EVENTS) {
      this.eventsSinceSizeCheck = 0;
      const estimatedRawSize = JSON.stringify(this.currentChunkEvents).length;
      if (estimatedRawSize >= TARGET_CHUNK_RAW_BYTES) {
        void this.flushCurrentChunk();
      }
    }

    if (this.sessionManager.isExpired()) {
      this.stop('normal');
    }
  }

  private onSessionEvent(event: SessionEvent): void {
    const state = this.sessionManager.getState();
    if (!state || this.ended) return;
    if (this.sessionEventCount >= SESSION_EVENTS_MAX_PER_SESSION) return;
    this.sessionEventCount += 1;

    if (state.mode === 'buffered') {
      this.sessionEventsRing?.add(event, event.ts);
      return;
    }

    this.pendingSessionEvents.push(event);
    if (this.pendingSessionEvents.length >= SESSION_EVENTS_BATCH_SIZE) {
      this.flushSessionEvents();
    }
  }

  private flushSessionEvents(keepalive = false): void {
    if (this.pendingSessionEvents.length === 0) return;
    const state = this.sessionManager.getState();
    const events = this.pendingSessionEvents;
    this.pendingSessionEvents = [];
    if (!state || state.mode !== 'recording') return;
    this.sendEventsBatched(state.session_id, events, keepalive);
  }

  /** The regular flush never exceeds the batch size, but a drained ring buffer can — slice to the contract's per-request max. */
  private sendEventsBatched(sessionId: string, events: SessionEvent[], keepalive = false): void {
    for (let i = 0; i < events.length; i += SESSION_EVENTS_MAX_PER_REQUEST) {
      this.transport.sendEvents(
        sessionId,
        events.slice(i, i + SESSION_EVENTS_MAX_PER_REQUEST),
        keepalive,
      );
    }
  }

  private async flushCurrentChunk(): Promise<void> {
    if (this.currentChunkEvents.length === 0) return;
    const events = this.currentChunkEvents;
    this.currentChunkEvents = [];
    this.eventsSinceSizeCheck = 0;
    await this.uploadEventsInBudget(events).catch(() => {
      // swallow — best-effort; the backend's idle sweeper finalizes the session without this chunk
    });
  }

  /** Splits recursively until every batch's gzipped size fits under the backend's max_chunk_bytes. */
  private async uploadEventsInBudget(events: unknown[]): Promise<void> {
    if (events.length === 0) return;
    const state = this.sessionManager.getState();
    if (!state) return;

    const gzipped = await gzipJson(events);

    // State may have been cleared (e.g. by stop()) during the await above.
    if (!this.sessionManager.getState()) return;

    const limits = this.sessionManager.getLimits();

    if (gzipped.byteLength > limits.max_chunk_bytes && events.length > 1) {
      const mid = Math.floor(events.length / 2);
      await this.uploadEventsInBudget(events.slice(0, mid));
      await this.uploadEventsInBudget(events.slice(mid));
      return;
    }

    const chunkTs = this.lastEventTimestamp(events);
    const seq = this.sessionManager.allocateSeq();
    this.transport.enqueueChunk(state.session_id, seq, gzipped, chunkTs);
  }

  private lastEventTimestamp(events: unknown[]): number {
    const last = events[events.length - 1] as RRwebEventLike | undefined;
    return last?.timestamp ?? Date.now();
  }

  private onErrorCaptured(): void {
    const state = this.sessionManager.getState();
    if (!state || state.mode !== 'buffered') return;

    this.sessionManager.promoteToRecording();
    const bufferedEvents = this.ringBuffer?.drain() ?? [];
    this.ringBuffer = null;
    const bufferedSessionEvents = (this.sessionEventsRing?.drain() ?? []) as SessionEvent[];
    this.sessionEventsRing = null;

    // Chunks may upload before init lands or even succeeds — the backend
    // accepts chunks (and session events) for a not-yet-initialized session
    // and enriches it later.
    if (!state.init_sent) void this.sendInit(state.session_id, state.started_at);
    void this.uploadEventsInBudget(bufferedEvents);
    this.sendEventsBatched(state.session_id, bufferedSessionEvents);
  }

  /** Best-effort flush of whatever's queued, via keepalive — called on visibilitychange/pagehide. Never blocks unload. */
  private flushForUnload(): void {
    if (this.ended) return;
    const state = this.sessionManager.getState();
    if (!state || state.mode !== 'recording') return;

    this.flushSessionEvents(true);

    if (this.currentChunkEvents.length === 0) return;

    const events = this.currentChunkEvents;
    this.currentChunkEvents = [];
    void gzipJson(events)
      .then((gzipped) => {
        const limits = this.sessionManager.getLimits();
        if (gzipped.byteLength > limits.max_chunk_bytes) return; // best-effort — accept the gap, don't split at unload time
        const seq = this.sessionManager.allocateSeq();
        this.transport.sendChunkKeepalive(
          state.session_id,
          seq,
          gzipped,
          this.lastEventTimestamp(events),
        );
      })
      .catch(() => {
        // swallow — the backend's idle sweeper finalizes the session regardless
      });
  }

  /** Stops recording and signals the backend the session is over (best-effort). Safe to call more than once. */
  stop(reason: StreamEndReason): void {
    if (this.ended) return;
    this.ended = true;

    this.stopRecorder?.();
    this.stopEventCapture?.();
    if (this.flushTimer) clearInterval(this.flushTimer);
    if (this.sessionEventsFlushTimer) clearInterval(this.sessionEventsFlushTimer);
    if (this.durationTimer) clearTimeout(this.durationTimer);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.handlePageHide);

    setReplaySessionId(undefined);
    setErrorCapturedHook(undefined);
    setStopRequestedHook(undefined);

    const state = this.sessionManager.getState();
    // A session still in 'buffered' mode was never promoted/init'd — nothing exists on the backend to end.
    if (!state || state.mode === 'buffered') {
      this.sessionManager.end();
      return;
    }

    // Inline flush: allocate seq now (session state is still valid), gzip +
    // enqueue happen async after sessionManager.end(). This avoids the async
    // gap between gzipJson and allocateSeq that would hit null state.
    if (this.currentChunkEvents.length > 0) {
      const pendingEvents = this.currentChunkEvents;
      this.currentChunkEvents = [];
      const seq = this.sessionManager.allocateSeq();
      const lastTs = this.lastEventTimestamp(pendingEvents);
      void gzipJson(pendingEvents).then((gzipped) => {
        this.transport.enqueueChunk(state.session_id, seq, gzipped, lastTs);
      });
    }

    this.flushSessionEvents();

    this.transport.endSession(state.session_id, {
      ended_at: Date.now(),
      last_seq: Math.max(0, state.next_seq - 1),
      chunk_count: state.next_seq,
      reason,
    });

    this.sessionManager.end();
  }
}
